import type { ReactNode } from 'react';
import { Trans } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Card } from '@actual-app/components/card';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { FinancialText } from '#components/FinancialText';
import type { ReadyFinancialDashboard } from '#components/product-finance/client';
import {
  formatProductDate,
  formatProductMoney,
} from '#components/product-finance/money';
import type { ProductMoney } from '#components/product-finance/money';

import { SummaryAmount } from './dashboard-shared';

export function SafeToSpendHero({
  dashboard,
  isDetailOpen,
  onToggleDetail,
}: {
  dashboard: ReadyFinancialDashboard;
  isDetailOpen: boolean;
  onToggleDetail: () => void;
}) {
  const safe = dashboard.safeToSpend;
  return (
    <Card
      style={{
        margin: 0,
        border: 0,
        backgroundColor: theme.noticeBackground,
      }}
    >
      <View style={{ padding: 22, gap: 12 }}>
        <span style={{ color: theme.pageTextSubdued, fontWeight: 600 }}>
          <Trans>Available now</Trans>
        </span>
        <FinancialText
          as="strong"
          style={{ fontSize: 40, lineHeight: 1.05, color: theme.pageText }}
        >
          {formatProductMoney(safe.rawSafeToSpend)}
        </FinancialText>
        <span>
          <Trans>Until</Trans>: {formatProductDate(safe.throughDate)}
        </span>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 18 }}>
          <SummaryAmount
            label={<Trans>Current Cash</Trans>}
            money={dashboard.currentCash}
          />
          <SummaryAmount
            label={<Trans>Protected</Trans>}
            money={safe.totalProtected}
          />
        </View>
        <Button
          variant="bare"
          onPress={onToggleDetail}
          style={{ alignSelf: 'flex-start' }}
        >
          {isDetailOpen ? (
            <Trans>Hide details</Trans>
          ) : (
            <Trans>View details</Trans>
          )}
        </Button>
        {isDetailOpen && <SafeToSpendBreakdown dashboard={dashboard} />}
      </View>
    </Card>
  );
}

export function SafeToSpendBreakdown({
  dashboard,
}: {
  dashboard: ReadyFinancialDashboard;
}) {
  const safe = dashboard.safeToSpend;
  const commitmentNames = new Map(
    [
      ...dashboard.pendingCurrentCycleCommitments,
      ...dashboard.futureCycleCommitments,
      ...dashboard.upcomingCommitments,
      ...dashboard.overdueCommitments,
    ].map(item => [item.occurrence.id, item.occurrence.name] as const),
  );
  return (
    <View
      style={{
        gap: 7,
        paddingTop: 12,
        borderTop: `1px solid ${theme.cardBorder}`,
      }}
    >
      {safe.commitmentBreakdown.map(item => (
        <BreakdownRow
          key={item.sourceId}
          label={commitmentNames.get(item.sourceId) ?? item.sourceId}
          money={item.amount}
        />
      ))}
      <BreakdownRow
        label={<Trans>Minimum cash buffer</Trans>}
        money={safe.protectedMinimumCashBuffer}
      />
      <BreakdownRow
        label={<Trans>Goal contributions</Trans>}
        money={safe.protectedGoalContributions}
      />
      <BreakdownRow
        label={<Trans>Liquidity protection</Trans>}
        money={safe.protectedLiquidity}
      />
      <BreakdownRow
        label={<Trans>Total protected</Trans>}
        money={safe.totalProtected}
        isStrong
      />
    </View>
  );
}

export function BreakdownRow({
  label,
  money,
  isStrong = false,
}: {
  label: ReactNode;
  money: ProductMoney;
  isStrong?: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 12,
        fontWeight: isStrong ? 700 : undefined,
      }}
    >
      <span>{label}</span>
      <FinancialText>{formatProductMoney(money)}</FinancialText>
    </View>
  );
}
